import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './styles/App.css';
import { useVisiblePolling } from './hooks/useVisiblePolling';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PaymentConfirm from './pages/PaymentConfirm';
import Dashboard from './pages/Dashboard';
import CreateTournamentWizard from './pages/CreateTournamentWizard';
import TournamentDetail from './pages/TournamentDetail';
import TournamentSearch from './pages/TournamentSearch';
import JoinGroup from './pages/JoinGroup';
import Profile from './pages/Profile';
import Messages from './pages/Messages';
import Chat from './pages/Chat';
import GroupActivity from './pages/GroupActivity';
import Actu from './pages/Actu';
import Live from './pages/Live';
import Notifications from './pages/Notifications';
import CreatePartnerSearch from './pages/CreatePartnerSearch';
import CompleteProfile from './pages/CompleteProfile';
import Navigation from './components/Navigation';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const ORGA_URL = process.env.REACT_APP_ORGANIZER_API_URL || 'http://localhost:5001';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [chatUnread, setChatUnread] = useState(0);
  const [liveActive, setLiveActive] = useState(false);

  const fetchLive = async () => {
    const t = localStorage.getItem('token');
    if (!t) return;
    try {
      const res = await axios.get(`${ORGA_URL}/api/me/live`, { headers: { Authorization: `Bearer ${t}` } });
      setLiveActive(!!res.data.live);
    } catch { /* ignore */ }
  };

  const fetchChatUnread = async () => {
    const t = localStorage.getItem('token');
    if (!t) return;
    try {
      const res = await axios.get(`${API_URL}/api/messages/unread-count`, { headers: { Authorization: `Bearer ${t}` } });
      setChatUnread(res.data.count || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const token    = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setIsLoggedIn(true);
      setUser(JSON.parse(userData));
      axios.get(`${API_URL}/api/auth/profile`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          localStorage.setItem('user', JSON.stringify(res.data));
          setUser(res.data);
        })
        .catch(() => { /* keep cached data if offline */ });
      fetchChatUnread();
      fetchLive();
    }
    setLoading(false);
  }, []); // eslint-disable-line

  // Pollings globaux suspendus quand l'app est en arrière-plan (batterie mobile).
  useVisiblePolling(fetchChatUnread, 30000, isLoggedIn);
  useVisiblePolling(fetchLive, 30000, isLoggedIn);

  // Scroll focused input above the virtual keyboard on mobile
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const handle = () => {
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      }
    };
    vv.addEventListener('resize', handle);
    return () => vv.removeEventListener('resize', handle);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsLoggedIn(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setUser(null);
  };

  const handleUserUpdate = (updatedUser) => {
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  if (loading) return <div className="loading">Chargement…</div>;

  // ponytail: profil Google incomplet (pas de genre/niveau) -> jeu de routes réduit,
  // tout mène à /completer-profil (marche aussi au refresh, pas seulement après login)
  const profileIncomplete = isLoggedIn && user && (!user.gender || !user.level);

  return (
    <Router>
      <div className="App">
        <Routes>
          {!isLoggedIn ? (
            <>
              <Route path="/login"              element={<Login    onLogin={handleLogin} />} />
              <Route path="/register"           element={<Register onLogin={handleLogin} />} />
              <Route path="/forgot-password"    element={<ForgotPassword />} />
              <Route path="/reset-password"     element={<ResetPassword />} />
              <Route path="/paiement-confirme"  element={<PaymentConfirm />} />
              <Route path="/rejoindre/:code"    element={<JoinGroup user={null} />} />
              <Route path="*"                   element={<Navigate to="/login" />} />
            </>
          ) : profileIncomplete ? (
            <>
              <Route path="/completer-profil"   element={<CompleteProfile  user={user} onUserUpdate={handleUserUpdate} />} />
              <Route path="/paiement-confirme"  element={<PaymentConfirm />} />
              <Route path="*"                   element={<Navigate to="/completer-profil" />} />
            </>
          ) : (
            <>
              <Route path="/dashboard"          element={<Dashboard        user={user} onLogout={handleLogout} chatUnread={chatUnread} />} />
              <Route path="/groups"             element={<Navigate to="/profile?tab=groups" replace />} />
              <Route path="/tournaments/:tournamentId"         element={<TournamentDetail user={user} />} />
              <Route path="/tournaments/:tournamentId/edit"    element={<CreateTournamentWizard user={user} />} />
              <Route path="/creer"              element={<CreateTournamentWizard user={user} />} />
              <Route path="/recherche"          element={<TournamentSearch user={user} onLogout={handleLogout} chatUnread={chatUnread} />} />
              <Route path="/profile"            element={<Profile          user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />} />
              <Route path="/profil/:userId"    element={<Profile          user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />} />
              <Route path="/messages"           element={<Messages         user={user} onLogout={handleLogout} />} />
              <Route path="/messages/:convId"   element={<Chat             user={user} onChatRead={fetchChatUnread} />} />
              <Route path="/nos-tournois"       element={<GroupActivity    user={user} onLogout={handleLogout} />} />
              <Route path="/live"               element={<Live             user={user} />} />
              <Route path="/actu"               element={<Actu             user={user} />} />
              <Route path="/notifications"      element={<Notifications    user={user} />} />
              <Route path="/creer/partenaire"   element={<CreatePartnerSearch user={user} />} />
              <Route path="/reset-password"     element={<ResetPassword />} />
              <Route path="/paiement-confirme"  element={<PaymentConfirm />} />
              <Route path="/rejoindre/:code"    element={<JoinGroup        user={user} />} />
              <Route path="/completer-profil"   element={<CompleteProfile  user={user} onUserUpdate={handleUserUpdate} />} />
              <Route path="/"                   element={<Navigate to="/dashboard" />} />
              <Route path="*"                   element={<Navigate to="/dashboard" />} />
            </>
          )}
        </Routes>

        {isLoggedIn && <Navigation chatUnread={chatUnread} liveActive={liveActive} />}
      </div>
    </Router>
  );
}

export default App;
